// Единый диалог приложения (замечание владельца 2026-09-07: системные окна браузера
// выглядят чужеродно). Заменяет window.prompt / confirm / alert одним оформлением:
//   const name = await ask('Название набора фильтров');
//   if (await confirmBox('Удалить набор?')) …
//   await alertBox('Сообщение');
// Работает императивно, чтобы вызовы в коде экранов остались однострочными:
// диалог живёт в собственном корне React, примонтированном к body, и возвращает
// обещание с ответом (null / false — отказ). Enter — подтвердить, Esc — отменить.
import { useEffect, useRef, useState } from 'react';
import { createRoot, type Root } from 'react-dom/client';

type Vid = 'ask' | 'confirm' | 'alert';
type Zapros = {
  vid: Vid; text: string; podpis?: string; def?: string;
  ok: string; cancel?: string; opasno?: boolean;
  done: (v: string | boolean | null) => void;
};

let root: Root | null = null;
let pokazat: ((z: Zapros | null) => void) | null = null;
// первый запрос за сессию: корень React монтируется не мгновенно, и `pokazat` ещё пуст —
// запрос ждёт здесь, DialogHost забирает его при первом показе (найдено прогоном 2026-10-07:
// окно «Название нового проекта» не появлялось с первого раза)
let ozhidaet: Zapros | null = null;

function mount() {
  if (root) return;
  const el = document.createElement('div');
  el.id = 'plotDialog';
  document.body.appendChild(el);
  root = createRoot(el);
  root.render(<DialogHost />);
}

function DialogHost() {
  const [z, setZ] = useState<Zapros | null>(null);
  pokazat = setZ;
  useEffect(() => { if (ozhidaet) { const o = ozhidaet; ozhidaet = null; setZ(o); } }, []);
  if (!z) return null;
  return <Dialog z={z} onClose={() => setZ(null)} />;
}

function Dialog({ z, onClose }: { z: Zapros; onClose: () => void }) {
  const [v, setV] = useState(z.def || '');
  const input = useRef<HTMLInputElement>(null);
  const okno = useRef<HTMLDivElement>(null);
  // без поля ввода фокус — на само окно, иначе Enter/Esc уходили странице (2026-10-16, Delete в «Работах»)
  useEffect(() => { if (input.current) { input.current.focus(); input.current.select(); } else okno.current?.focus(); }, []);

  function otvet(soglasen: boolean) {
    onClose();
    if (z.vid === 'ask') z.done(soglasen ? v.trim() : null);
    else if (z.vid === 'confirm') z.done(soglasen);
    else z.done(true);
  }
  return (
    <div className="dlg-fon" onMouseDown={e => { if (e.target === e.currentTarget) otvet(false); }}>
      <div className="dlg" role="dialog" aria-modal="true" ref={okno} tabIndex={-1} style={{ outline: 'none' }}
        onKeyDown={e => {
          if (e.key === 'Escape') { e.stopPropagation(); otvet(false); }
          if (e.key === 'Enter' && z.vid !== 'alert') { e.preventDefault(); otvet(true); }
        }}>
        <div className="dlg-text">{z.text}</div>
        {z.podpis && <div className="dlg-podpis">{z.podpis}</div>}
        {z.vid === 'ask' && (
          <input ref={input} className="dlg-input" value={v} autoComplete="off"
            onChange={e => setV(e.target.value)} />
        )}
        <div className="dlg-knopki">
          {z.vid !== 'alert' && (
            <button className="sec" onClick={() => otvet(false)}>{z.cancel || 'Отмена'}</button>
          )}
          <button className={z.opasno ? 'dlg-opasno' : ''} onClick={() => otvet(true)}>{z.ok}</button>
        </div>
      </div>
    </div>
  );
}

function pokazatDialog(z: Omit<Zapros, 'done'>): Promise<string | boolean | null> {
  mount();
  return new Promise(resolve => {
    const zapros: Zapros = { ...z, done: resolve };
    if (pokazat) pokazat(zapros);
    else ozhidaet = zapros;
  });
}

/** Поле ввода: вернёт строку или null, если отказались. Пустая строка — тоже null. */
export async function ask(text: string, def = '', opts: { podpis?: string; ok?: string } = {}) {
  const v = await pokazatDialog({ vid: 'ask', text, def, podpis: opts.podpis, ok: opts.ok || 'Сохранить' });
  const s = typeof v === 'string' ? v.trim() : '';
  return s || null;
}

/** Да/нет: вернёт true, если согласились. */
export async function confirmBox(text: string,
  opts: { podpis?: string; ok?: string; opasno?: boolean; cancel?: string } = {}) {
  return (await pokazatDialog({
    vid: 'confirm', text, podpis: opts.podpis, ok: opts.ok || 'Да', opasno: opts.opasno, cancel: opts.cancel,
  })) === true;
}

/** Сообщение с одной кнопкой. */
export async function alertBox(text: string, podpis?: string) {
  await pokazatDialog({ vid: 'alert', text, podpis, ok: 'Понятно' });
}
